import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { populateRelationshipFromRequest } from 'src/utils/populateRelationship';

import { normalizeFbmResource } from '../../models/fbm-resource/normalizers';
import { Service } from '../appointment/_types';
import { PaymentPlan } from '../payment/_types';
import { User } from '../user/_types';
import { fetchUser, fetchUsers } from '../user/user';
import { normalizeUserPackage } from '../user-package/normalizers';
import { UserPackage } from '../user-package/user-package';

import {
  denormalizeScheduledMembership,
  denormalizeVisit,
  normalizeScheduledAppointment,
  normalizeScheduledMembership,
  normalizeVisit,
} from './normalizers';

import type {
  ScheduledAppointment,
  ScheduledMembership,
  Visit,
  VisitStatus,
} from './_types';

interface FetchVisitsOptions {
  userId?: User['id'];
  withUser: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
  page?: number;
  perPage?: number;
  withPackage?: boolean;
  filters?: {
    locationId?: number | string | null;
    trainerId?: User['id'];
    serviceId?: Service['id'];
    completedVisits?: boolean;
    checkIns?: boolean;
    paidStatus?: 'paid' | 'not_paid' | 'not_charged';
    status?: VisitStatus[];
    allVisits?: boolean;
    membershipId?: number;
    dateRange?: {
      start: string;
      end: string;
    };
  };
}

interface FetchVisitsResponse {
  visits: Visit[];
  total: number;
}

export const fetchVisits = async ({
  userId,
  withUser,
  sortBy = 'completed_at',
  sortDir = 'desc',
  search,
  page = 1,
  perPage = 20,
  withPackage = false,
  filters = {},
}: FetchVisitsOptions): Promise<FetchVisitsResponse> => {
  const params = {
    page,
    per: perPage,
    for_user: true,
    'q[s]': `${sortBy} ${sortDir}`,
    ...(search && {
      'q[user_first_name_or_user_last_name_or_user_email_or_appointment_service_name_cont]':
        search,
    }),
    ...(userId && { 'q[user_id_or_chaperone_user_id_eq]': userId }),
    ...(filters.completedVisits && { active_visits: true }),
    ...(filters.membershipId && {
      'q[membership_id_eq]': filters.membershipId,
    }),
    ...(filters.allVisits
      ? {}
      : filters.checkIns
      ? { 'q[check_in_not_null]': true }
      : { 'q[check_in_null]': true }),
    ...(filters.dateRange && {
      start_date: getUnixTime(new Date(filters.dateRange.start)),
      end_date: getUnixTime(endOfDay(new Date(filters.dateRange.end))),
    }),
    ...(filters.locationId && { 'q[has_location]': filters.locationId }),
    ...(filters.trainerId && { 'q[has_trainer]': filters.trainerId }),
    ...(filters.serviceId && {
      'q[appointment_service_id_eq]': filters.serviceId,
    }),
    ...(filters.status?.length
      ? {
          'q[has_status]': Array.isArray(filters.status)
            ? filters.status.join(',')
            : filters.status,
        }
      : {}),
    ...(filters.paidStatus
      ? {
          'q[cancelled_at_or_noshow_at_or_completed_at_not_null]': 1,
          ...(filters.paidStatus === 'paid'
            ? {
                'q[paid_true]': 1,
                'q[paid_by_package_id_not_null]': 1,
              }
            : filters.paidStatus === 'not_paid'
            ? { 'q[paid_false]': 1 }
            : filters.paidStatus === 'not_charged'
            ? {
                'q[paid_true]': 1,
                'q[paid_by_package_id_null]': 1,
              }
            : {}),
        }
      : {}),
  };

  const response = await api.get(`/api/v4/fbm/visits`, { params });

  const visits = response.data.visit?.map((v) => normalizeVisit(v));
  if (!withUser) return visits;

  let visitsWithUsers = await populateRelationshipFromRequest({
    list: visits,
    idKey: 'userId',
    newKey: 'user',
    func: (ids) => fetchUsers({ ids, withAlerts: true, withDependents: true }),
  });

  // Fetch first visits for all users
  const userIds = visitsWithUsers.map((visit) => visit.userId);
  const firstVisits = await fetchFirstVisits(userIds);

  // Add firstVisit flag to each visit
  visitsWithUsers = visitsWithUsers.map((visit) => ({
    ...visit,
    firstVisit: firstVisits[visit.userId] === visit.id,
  }));

  if (withPackage) {
    visitsWithUsers = await Promise.all(
      visitsWithUsers.map(async (visit) => ({
        ...visit,
        package: await fetchVisitPackage(visit.id),
      }))
    );
  }

  return { visits: visitsWithUsers, total: response.data.meta.total };
};

interface FetchVisitOptions {
  withUser: boolean;
}

export const fetchVisit = async (
  id: Visit['id'],
  { withUser }: FetchVisitOptions
): Promise<Visit> => {
  const response = await api.get(`/api/v4/fbm/visits/${id}`);
  const visit = normalizeVisit(response.data);
  if (!withUser || !visit.userId) {
    return visit;
  }
  const user = await fetchUser({
    id: visit.userId,
    withAlerts: true,
    withDependents: true,
  });
  return {
    ...visit,
    user,
  };
};

export const fetchResources = async (visit: Visit) => {
  const response = await api.get(`/api/v4/fbm/resources`, {
    params: { visit_id: visit.id, fetch_all: true },
  });

  return response.data.map((r) => normalizeFbmResource(r));
};

export interface UserPackageWithPaymentPlan extends UserPackage {
  paymentPlan?: PaymentPlan; // You can specify a more precise type for the payment plan
}

export const fetchVisitPackage = async (
  id: Visit['id']
): Promise<UserPackageWithPaymentPlan> => {
  const response = await api.get(`/api/v4/fbm/visits/${id}/package`, {
    params: { include_plan: true },
  });
  const pkg: UserPackageWithPaymentPlan = normalizeUserPackage(
    response.data.user_package || response.data
  );
  if (response.data.payment_plan) {
    const plan = camelcaseKeys(response.data.payment_plan, { deep: true });
    pkg.paymentPlan = plan;
  }
  return pkg;
};

export const resetVisit = async (id: Visit['id']): Promise<void> => {
  const response = await api.put(`/api/v4/fbm/visits/${id}/reset`);
  return response.data;
};

export const createVisit = async (visit: Partial<Visit>): Promise<Visit> => {
  const response = await api.post(
    `/api/v4/fbm/visits/`,
    denormalizeVisit(visit)
  );

  return normalizeVisit(response.data);
};

export const updateVisit = async (
  visit: Partial<Visit> & {
    cancelRecurring?: boolean;
  }
): Promise<Visit> => {
  const response = await api.put(`/api/v4/fbm/visits/${visit.id}`, {
    visit: denormalizeVisit(visit),
  });

  return normalizeVisit(response.data);
};

export const deleteVisit = async (id: Visit['id']): Promise<Visit> => {
  const response = await api.delete(`/api/v4/fbm/visits/${id}`);
  return normalizeVisit(response.data);
};

export const bulkChargeDefaultPackage = async ({
  visitIds,
  completeVisit,
}: {
  visitIds: Visit['id'][];
  completeVisit: boolean;
}): Promise<Visit> => {
  try {
    const response = await api.post(`/api/v4/fbm/visits/bulk_charge`, {
      visit_ids: visitIds,
      complete_visit: completeVisit,
    });
    return normalizeVisit(response.data);
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error bulk charging visits.'));
  }
};

export const bulkUpdateVisits = async ({
  visitIds,
  status,
  cancelRecurring,
}: {
  visitIds: Visit['id'][];
  status: VisitStatus;
  cancelRecurring: boolean;
}): Promise<Visit> => {
  const response = await api.post(`/api/v4/fbm/visits/bulk_update`, {
    visit_ids: visitIds,
    status: status,
    cancel_recurring: cancelRecurring,
  });
  return normalizeVisit(response.data);
};

interface FetchFirstVisitsResponse {
  [userId: User['id']]: Visit['id'];
}

// Add this new function to fetch first visits
export const fetchFirstVisits = async (
  userIds: User['id'][]
): Promise<FetchFirstVisitsResponse> => {
  const response = await api.get('/api/v4/fbm/visits/first_visit', {
    params: { user_ids: userIds.join(',') },
  });

  return response.data;
};

export const fetchVisitAvailablePackages = async (
  visitId: Visit['id']
): Promise<UserPackage[]> => {
  const response = await api.get(
    `/api/v4/fbm/visits/${visitId}/available_packages`
  );

  return response.data.map((pkg: any) => normalizeUserPackage(pkg));
};

export const fetchScheduledMemberships = async (
  userId: User['id']
): Promise<ScheduledMembership[]> => {
  const response = await api.get('/api/v4/fbm/scheduled_appointment_members', {
    params: {
      'q[user_id_eq]': userId,
      fetch_all: true,
    },
  });

  const memberships = response.data.map((membership: any) =>
    normalizeScheduledMembership(membership)
  );

  return populateRelationshipFromRequest({
    list: memberships,
    idKey: 'scheduledAppointmentId',
    newKey: 'scheduledAppointment',
    func: (ids) => Promise.all(ids.map((id) => fetchScheduledAppointment(id))),
  });
};

export const fetchScheduledAppointment = async (
  id: User['id']
): Promise<ScheduledAppointment> => {
  const response = await api.get(`/api/v4/fbm/scheduled_appointments/${id}`);
  return normalizeScheduledAppointment(response.data);
};

export const deleteScheduledMembership = async (
  id: ScheduledMembership['id']
): Promise<void> => {
  await api.delete(`/api/v4/fbm/scheduled_appointment_members/${id}`);
};

export const updateScheduledMembership = async (
  id: ScheduledMembership['id'],
  membership: Partial<ScheduledMembership>
): Promise<ScheduledMembership> => {
  const response = await api.put(
    `/api/v4/fbm/scheduled_appointment_members/${id}`,
    denormalizeScheduledMembership(membership)
  );
  return normalizeScheduledMembership(response.data);
};
